import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
 HiOutlineChevronDown,
 HiOutlineLogout,
 HiOutlineUser,
} from "react-icons/hi";
import keycloak from "../keycloak";
import { fetchUserProfile, clearProfileCache } from "../services/userManagementApi";

const pageTitles = {
 "/dashboard": "Dashboard",
 "/master-data/states": "States",
 "/master-data/districts": "Districts",
 "/master-data/blocks": "Blocks",
 "/master-data/schools": "Schools",
 "/cycles/continuous": "Continuous Cycles",
 "/cycles/quarterly": "Quarterly Cycles",
 "/user-management/users": "Users",
 "/competency/list": "Competency",
 "/competency/category": "Category",
 "/competency/header": "Header",
 "/question-bank/sets": "Sets & Questions",
 "/profile": "Profile",
};

const pageDescriptions = {
 "/dashboard": "Overview of your system metrics and activity",
 "/master-data/states": "Manage state-level geographical data",
 "/master-data/districts": "Manage district-level geographical data",
 "/master-data/blocks": "Manage block data",
 "/master-data/schools": "Manage school-level geographical data",
 "/cycles/continuous": "Manage continuous assessment cycles",
 "/cycles/quarterly": "Manage quarterly assessment cycles",
 "/user-management/users": "Manage user accounts and role assignments",
 "/competency/list": "Manage competencies across states",
 "/competency/category": "Manage competency category weightage",
 "/competency/header": "Manage competency headers and sub-headers",
 "/question-bank/sets": "Manage competency sets and their questions",
 "/profile": "View and manage your account settings",
};

function Header() {
 const [dropdownOpen, setDropdownOpen] = useState(false);
 const [profile, setProfile] = useState(null);
 const dropdownRef = useRef(null);
 const location = useLocation();
 const navigate = useNavigate();

 const pageTitle = pageTitles[location.pathname] || "";
 const pageDescription = pageDescriptions[location.pathname] || "";

 useEffect(() => {
 const loadProfile = async () => {
 try {
 const data = await fetchUserProfile();
 setProfile(data);
 } catch (err) {
 console.error("Failed to fetch profile", err);
 }
 };
 loadProfile();
 }, []);

 useEffect(() => {
 function handleClickOutside(e) {
 if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
 setDropdownOpen(false);
 }
 }
 document.addEventListener("mousedown", handleClickOutside);
 return () => document.removeEventListener("mousedown", handleClickOutside);
 }, []);

 const capitalize = (text) =>
 text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

 // Consistent short-form role labels, matching the convention used in
 // pages/Users.js (ROLE_OPTIONS: ADMIN, SPL, PL, PM, PO).
 const roleLabels = {
 ADMIN: "ADMIN",
 SPL: "SPL",
 PL: "PL",
 PM: "PM",
 PO: "PO",
 };

 const getRoleLabel = (role) => {
 if (!role) return "";
 const key = role.toUpperCase();
 return roleLabels[key] || capitalize(role);
 };

 const avatarLetter = profile?.username?.charAt(0).toUpperCase() || "";
 const displayRole = getRoleLabel(profile?.roles?.[0]);

 const handleLogout = () => {
 localStorage.removeItem(`sloSetFormDraft_${keycloak.subject}`);
 clearProfileCache();
 keycloak.logout({ redirectUri: window.location.origin });
 };

 const handleProfileClick = (e) => {
 e.stopPropagation();
 setDropdownOpen(false);
 navigate("/profile");
 };

 return (
 <header className="sticky top-0 z-50 bg-white border-b border-[#e5e7eb]">
 <div className="flex items-center justify-between h-14 px-6">
 {/* Left — Title & Description */}
 <div className="flex flex-col">
 <h1 className="m-0 text-base font-semibold text-[#24292f] tracking-tight leading-tight">
 {pageTitle}
 </h1>
 <p className="m-0 text-[12px] text-[#57606a] font-normal mt-0.5 leading-tight">
 {pageDescription}
 </p>
 </div>

 {/* Right — Actions */}
 <div className="flex items-center gap-2">
 {/* Profile Dropdown */}
 <div
 className="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-md transition-all duration-150 hover:bg-[#f3f4f6] relative"
 ref={dropdownRef}
 onClick={() => setDropdownOpen(!dropdownOpen)}
 >
 <div className="w-7 h-7 bg-[#0078d4] rounded-md flex items-center justify-center font-semibold text-xs text-white flex-shrink-0">
 {avatarLetter}
 </div>
 <div className="flex flex-col">
 <span className="text-[13px] font-medium text-[#24292f] leading-tight">
 {profile?.username || "Loading..."}
 </span>
 <span className="text-[10px] text-[#8b949e] font-normal">
 {displayRole}
 </span>
 </div>
 <HiOutlineChevronDown
 className={`text-[10px] text-[#8b949e] transition-transform duration-150 ${
 dropdownOpen ? "rotate-180" : ""
 }`}
 />

 {/* Dropdown Menu */}
 {dropdownOpen && (
 <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-[#e5e7eb] rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.12)] min-w-[220px] p-1.5 z-[200] animate-[fadeIn_0.12s_ease-out]">
 {/* Profile Card */}
 <div className="flex items-center gap-3 px-3 py-2.5">
 <div className="w-9 h-9 bg-[#0078d4] rounded-md flex items-center justify-center font-semibold text-sm text-white flex-shrink-0">
 {avatarLetter}
 </div>
 <div className="flex flex-col min-w-0">
 <span className="text-sm font-medium text-[#24292f] leading-snug truncate">
 {profile?.username}
 </span>
 <span className="text-xs text-[#8b949e] leading-snug truncate">
 {profile?.email}
 </span>
 </div>
 </div>

 {/* Role Badge */}
 <div className="px-3 pb-2">
 <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#eff6ff] text-[#0078d4] rounded">
 {displayRole}
 </span>
 </div>

 <div className="h-px bg-[#e5e7eb] mx-1" />

 <button
 className="flex items-center gap-2.5 w-full px-3 py-2 mt-1 text-[13px] font-medium text-[#57606a] bg-transparent border-none rounded-md cursor-pointer text-left transition-all duration-150 hover:bg-[#f3f4f6] hover:text-[#24292f]"
 onClick={handleProfileClick}
 >
 <HiOutlineUser className="text-sm" />
 My Profile
 </button>

 <div className="h-px bg-[#e5e7eb] mx-1" />

 <button
 className="flex items-center gap-2.5 w-full px-3 py-2 mt-1 mb-0.5 text-[13px] font-medium text-red-600 bg-transparent border-none rounded-md cursor-pointer text-left transition-all duration-150 hover:bg-red-50"
 onClick={handleLogout}
 >
 <HiOutlineLogout className="text-sm" />
 Sign Out
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 </header>
 );
}

export default Header;
